import { memo, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ReactComponent as WarningIcon } from "../../../../assets/content-overview-images/warnings.svg";

import { ReactComponent as TaskIcon } from "../../../../assets/content-overview-images/dailyTasks.svg";
import { ReactComponent as ArticleIcon } from "../../../../assets/content-overview-images/articles.svg";
import { ReactComponent as FactsIcon } from "../../../../assets/content-overview-images/facts.svg";
import { ReactComponent as ScenariosIcon } from "../../../../assets/content-overview-images/scenarios.svg";
import { ReactComponent as PlansIcon } from "../../../../assets/content-overview-images/plans.svg";

import { ContentOverviewBlock } from "../../../../components/content-overview-block/content-overview-block";
import { Drawer } from "../../../../components/drawer/drawer";
import { DrawerHeader } from "../../../../components/drawer/drawer-header";

import encodeSvgWithColor from "../../../../common/utils/encodeSvgWithColor";
import "./content-warnings.css";

const pluralize = (n, one, many = null) => {
  const x = Number(n || 0);
  if (x === 1) return one;
  return many || `${one}s`;
};

const TYPE_META = {
  dailyTask: {
    Icon: TaskIcon,
    label: "Task",
    buildHref: (projectCode, id) => `/${projectCode}/content/tasks/${id}`,
  },
  article: {
    Icon: ArticleIcon,
    label: "Article",
    buildHref: (projectCode, id) => `/${projectCode}/content/articles/${id}`,
  },
  fact: {
    Icon: FactsIcon,
    label: "FitFact",
    buildHref: (projectCode, id) => `/${projectCode}/content/fitfacts/${id}`,
  },
  scenario: {
    Icon: ScenariosIcon,
    label: "Scenario",
    buildHref: (projectCode, id) => `/${projectCode}/content/scenarios/${id}`,
  },
  onboardingSurvey: {
    Icon: ScenariosIcon,
    label: "Onboarding",
    buildHref: (projectCode, id) => `/${projectCode}/content/onboardings/${id}`,
  },
  plan: {
    Icon: PlansIcon,
    label: "Plan",
    buildHref: (projectCode, id) => `/${projectCode}/content/plans/${id}`,
  },
};

const formatDate = (d) => {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(d));
  } catch {
    return String(d);
  }
};

const openNew = (href) => {
  if (!href) return;
  window.open(href, "_blank", "noopener,noreferrer");
};

function getTaskIconSrc(item) {
  if (!item?.icon) return null;

  try {
    return encodeSvgWithColor(item.icon, item.iconColor || "#000000");
  } catch (error) {
    console.error("Failed to encode task icon:", error);
    return null;
  }
}

const WarningItemsList = memo(({ items, projectCode }) => {
  const visible = Array.isArray(items) ? items : [];

  const handleOpen = (item) => {
    const id = item?.id;
    const type = item?.type;
    if (!id || !type) return;

    const meta = TYPE_META[type];
    if (!meta?.buildHref) return;

    openNew(meta.buildHref(projectCode, id));
  };

  return (
    <div className="content-warnings-list bordered">
      {visible.map((item) => {
        const meta = TYPE_META[item.type] || {};
        const Icon = meta.Icon;
        const typeLabel = meta.label || item.type || "Item";
        const isDailyTask = item.type === "dailyTask";

        const taskIconSrc = isDailyTask ? getTaskIconSrc(item) : null;
        const taskIconBackground = isDailyTask
          ? item.iconBackground || "transparent"
          : "transparent";

        const dateLabel = item?.date
          ? `last modified: ${formatDate(item.date)}`
          : "last modified: —";

        return (
          <div
            key={`${item.type}-${item.id}-${item.date || "nodate"}`}
            className="content-warnings-item"
            role="button"
            tabIndex={0}
            onClick={() => handleOpen(item)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") handleOpen(item);
            }}
            title={item.title || ""}
          >
            <div className="content-warnings-item-left">
              <div
                className="content-warnings-item-iconWrap"
                style={isDailyTask ? { background: taskIconBackground } : undefined}
              >
                {isDailyTask ? (
                  taskIconSrc ? (
                    <img
                      src={taskIconSrc}
                      alt={item.title || "Task icon"}
                      className="content-warnings-item-taskImage"
                    />
                  ) : (
                    <TaskIcon className="content-warnings-item-icon content-warnings-item-icon--plain" />
                  )
                ) : Icon ? (
                  <Icon className="content-warnings-item-icon" />
                ) : null}
              </div>
            </div>

            <div className="content-warnings-item-right">
              <div className="content-warnings-item-title">{item.title || "—"}</div>
              <div className="content-warnings-item-desc">
                {typeLabel}, {dateLabel}
              </div>
              {Array.isArray(item.brokenRefs) && item.brokenRefs.length ? (
                <div className="content-warnings-item-broken">
                  {`Missing: ${item.brokenRefs
                    .map((r) => (r.label ? `${r.refLabel} "${r.label}"` : r.refLabel))
                    .join(", ")}`}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}

      {visible.length === 0 ? (
        <div className="content-warnings-empty">No items</div>
      ) : null}
    </div>
  );
});

export const ContentWarnings = memo(
  ({ warningsData, loading, error, defaultOpen, onToggle }) => {
    const { projectCode } = useParams();
    const [drawer, setDrawer] = useState({ open: false, warning: null });

    const normalized = useMemo(() => {
      const modulesWithIssuesCount = Number(
        warningsData?.modulesWithIssuesCount || 0
      );
      const warnings = Array.isArray(warningsData?.warnings)
        ? warningsData.warnings
        : [];

      const items = warnings
        .filter((w) => Number(w?.count || 0) > 0)
        .map((w) => ({
          key: w.key,
          module: w.module || "Module",
          title: w.title || "Warning",
          message: w.message || "",
          count: Number(w.count || 0),
          assetType: w.assetType || null,
          items: Array.isArray(w.items) ? w.items : [],
        }));

      return { modulesWithIssuesCount, warnings: items };
    }, [warningsData]);

    const headerText =
      normalized.modulesWithIssuesCount > 0
        ? `${normalized.modulesWithIssuesCount} ${pluralize(
            normalized.modulesWithIssuesCount,
            "module"
          )} with configuration issues`
        : "No configuration issues";

    const openWarning = (w) => setDrawer({ open: true, warning: w });

    const tagText = (w) => w.message || w.title;

    return (
      <>
        <ContentOverviewBlock
          title="Configuration Warnings"
          icon={<WarningIcon />}
          defaultOpen={defaultOpen}
          onToggle={onToggle}
        >
          <div className="content-warnings">
            <div className="content-warnings-title">{headerText}</div>

            <div className="content-warnings-tags">
              {normalized.warnings.map((w) => (
                <button
                  key={w.key}
                  type="button"
                  className="content-warnings-tag"
                  onClick={() => openWarning(w)}
                  title={w.title}
                >
                  <span className="content-warnings-tag-module">{w.module}</span>
                  <span className="content-warnings-tag-text">
                    : {w.count} {tagText(w)}
                  </span>
                </button>
              ))}

              {!loading && normalized.warnings.length === 0 ? (
                <div className="content-warnings-empty">All good</div>
              ) : null}
            </div>

            {loading ? (
              <div className="content-warnings-hint">Loading…</div>
            ) : null}

            {error ? (
              <div className="content-warnings-hint error">
                Failed to load warnings
              </div>
            ) : null}
          </div>
        </ContentOverviewBlock>

        <Drawer
          open={drawer.open}
          closable={false}
          onClose={() => setDrawer({ open: false, warning: null })}
        >
          <DrawerHeader
            title={
              drawer.warning
                ? `${drawer.warning.module}: ${drawer.warning.count} ${tagText(
                    drawer.warning
                  )}`
                : "Warning"
            }
            onClose={() => setDrawer({ open: false, warning: null })}
          />

          <div className="content-warnings-drawer-body">
            <WarningItemsList
              items={drawer.warning?.items}
              projectCode={projectCode}
            />
          </div>
        </Drawer>
      </>
    );
  }
);